import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Grid2X2,
  LockKeyhole,
  Maximize2,
  Minimize2,
  X,
} from "lucide-react";
import styles from "./MathStudy.module.css";
import { getMathCourses, getMathLibrary } from "../../../services/mathService";

const DEFAULT_LIBRARY = {
  eyebrow: "Mathematics studio",
  title: "Choose a course.",
  emphasis: "Open a world.",
  intro: "A focused collection of visual, self-paced mathematics books designed for deliberate practice.",
  placeholderTitle: "Your next course",
  placeholderText: "Ready for a new cover uploaded from the course library.",
};

const BOOK_PAGES = [
  { kicker: "Chapter 01", title: "A calm start", body: "Build fluent mathematical thinking with short, focused lessons. Each chapter moves from an idea to a worked example and then to your own practice.", type: "intro" },
  { kicker: "Concept", title: "Functions tell a story", body: "A function maps one input to one output. Its graph lets you see the relationship before you calculate it.", formula: "f(x) = mx + c", type: "formula" },
  { kicker: "Worked example", title: "Find the gradient", body: "For the points (2, 5) and (6, 13), compare the vertical and horizontal changes.", formula: "m = (13 − 5) / (6 − 2) = 2", type: "worked" },
  { kicker: "Try it", title: "Your turn", body: "A line passes through (1, 4) and (5, 16). What is its gradient? Reveal a hint only when you need it.", type: "practice" },
  { kicker: "Recall", title: "Keep this close", body: "Gradient measures change in y for every one unit of x. A positive gradient rises from left to right.", type: "recap" },
  { kicker: "Next chapter", title: "Ready for graphs", body: "Continue to transformations, intercepts and modelling. Your place is saved automatically.", type: "next" },
];

function CourseCover({ course }) {
  return (
    <div
      className={styles.courseCover}
      style={{
        "--course-accent": course.color,
        ...(course.coverUrl ? { backgroundImage: `url(${course.coverUrl})` } : {}),
      }}
    >
      {/* <div className={styles.coverGlow} /> */}
      <div className={styles.coverRibbon}>
        <Bookmark size={13} className={styles.ribbonIcon} />
        <span>{course.level}</span>
      </div>
      <div className={styles.coverTop} />
      <div className={styles.coverBody}>
        <div className={styles.coverTitleBox}>
          <strong>{course.title}</strong>
          <span>{course.subtitle}</span>
        </div>
      </div>
      <div className={styles.coverMark}>∑</div>
    </div>
  );
}

function CourseGallery({ onOpen, library, courses }) {
  return (
    <main className={styles.page}>
      <section className={styles.galleryIntro}>
        <div>
          <p className={styles.eyebrow}>{library.eyebrow}</p>
          <p className={styles.introText}>{library.intro}</p>
        </div>
      </section>

      <section className={styles.gallery} aria-label="Mathematics courses">
        {courses.map((course, index) => (
          <button
            key={course.id || course._id}
            type="button"
            className={styles.courseCard}
            style={{ "--course-accent": course.color, "--card-delay": `${index * 70}ms` }}
            onClick={() => onOpen(course)}
          >
            <CourseCover course={course} />
            <div className={styles.courseMeta}>
              <div>
                <span className={styles.metaBookmark}>
                  <Bookmark size={11} className={styles.metaBookmarkIcon} />
                  {course.level} Course
                </span>
                <h2>{course.title}</h2>
              </div>
              <span className={styles.openCourse}>Open <ArrowRight size={16} /></span>
            </div>
            <div className={styles.courseDetails}><span><BookOpen size={14} /> {course.lessons} lessons</span><span><Clock3 size={14} /> {course.duration}</span></div>
          </button>
        ))}
        <div className={styles.libraryPlaceholder}>
          <div><span>+</span><strong>{library.placeholderTitle}</strong><p>{library.placeholderText}</p></div>
        </div>
      </section>
    </main>
  );
}

function BookPage({ page, number, course }) {
  return (
    <article className={`${styles.bookPage} ${styles[`page${page.type}`] || ""}`}>
      <div className={styles.pageRunningHead}><span>{course.level} Mathematics</span><span>{String(number).padStart(2, "0")}</span></div>
      <div className={styles.pageContent}>
        <p className={styles.pageKicker}>{page.kicker}</p>
        <h2>{page.title}</h2>
        <p>{page.body}</p>
        {page.formula && <div className={styles.formula}>{page.formula}</div>}
        {page.type === "worked" && <div className={styles.stepList}><span>01 · Identify change in y</span><span>02 · Identify change in x</span><span>03 · Divide to find m</span></div>}
        {page.type === "practice" && <button type="button" className={styles.hintButton}><LockKeyhole size={15} /> Reveal a hint</button>}
        {page.type === "next" && <button type="button" className={styles.continueButton}>Continue learning <ArrowRight size={16} /></button>}
      </div>
      <div className={styles.pageFooter}><span>Learn · test · reflect</span><span>{number}</span></div>
    </article>
  );
}

function PdfBookPage({ number, course }) {
  return (
    <article className={`${styles.bookPage} ${styles.pdfPage}`}>
      <div className={styles.pageRunningHead}><span>{course.level} Mathematics</span><span>{String(number).padStart(2, "0")}</span></div>
      <iframe title={`${course.title} page ${number}`} className={styles.pdfFrame} src={`${course.textbookUrl}#page=${number}&view=FitH&toolbar=0&navpanes=0`} />
      <div className={styles.pageFooter}><span>Digital textbook</span><span>{number}</span></div>
    </article>
  );
}

function BlankBookPage() {
  return <article className={`${styles.bookPage} ${styles.blankBookPage}`} aria-hidden="true" />;
}

function ImageBookPage({ page, number, course }) {
  return <article className={`${styles.bookPage} ${styles.imageBookPage}`}>
    <div className={styles.pageRunningHead}><span>{course.level} Mathematics</span><span>{String(number).padStart(2, "0")}</span></div>
    <img src={page.url} alt={`${course.title} page ${number}`} />
    <div className={styles.pageFooter}><span>Digital textbook</span><span>{number}</span></div>
  </article>;
}

function ImageSpread({ page, number, course }) {
  return <article className={styles.imageSpread}>
    <div className={styles.pageRunningHead}><span>{course.level} Mathematics</span><span>{String(number).padStart(2, "0")}–{String(number + 1).padStart(2, "0")}</span></div>
    <img src={page.url} alt={`${course.title} pages ${number} and ${number + 1}`} />
    <div className={styles.pageFooter}><span>Digital textbook</span><span>{number}–{number + 1}</span></div>
  </article>;
}

function buildImageSpreads(pages) {
  const spreads = [];
  let pendingSingle = null;
  pages.forEach((page) => {
    if (page.layout === "spread") {
      if (pendingSingle) { spreads.push({ left: pendingSingle }); pendingSingle = null; }
      spreads.push({ spread: page });
    } else if (pendingSingle) {
      spreads.push({ left: pendingSingle, right: page }); pendingSingle = null;
    } else {
      pendingSingle = page;
    }
  });
  if (pendingSingle) spreads.push({ left: pendingSingle });
  return spreads;
}

function PageSlot({ side, spreadIndex, course, contentPages, isPdfBook, isImageBook, imageSpreads }) {
  const leftIndex = spreadIndex * 2;
  const rightIndex = leftIndex + 1;
  const leftPageNumber = isPdfBook ? leftIndex + 2 : leftIndex + 1;
  const rightPageNumber = isPdfBook ? rightIndex + 2 : rightIndex + 1;
  const imageSpread = isImageBook ? imageSpreads[spreadIndex] : null;

  if (isImageBook && imageSpread?.spread) {
    return <ImageSpread page={imageSpread.spread} number={leftPageNumber} course={course} />;
  }

  if (side === "left") {
    if (isPdfBook) {
      return <PdfBookPage number={leftPageNumber} course={course} />;
    }
    if (isImageBook) {
      return imageSpread?.left ? (
        <ImageBookPage page={imageSpread.left} number={leftPageNumber} course={course} />
      ) : (
        <BlankBookPage />
      );
    }
    return contentPages[leftIndex] ? (
      <BookPage page={contentPages[leftIndex]} number={leftPageNumber} course={course} />
    ) : (
      <BlankBookPage />
    );
  }

  if (side === "right") {
    if (isPdfBook) {
      return rightPageNumber <= course.textbookPageCount ? (
        <PdfBookPage number={rightPageNumber} course={course} />
      ) : (
        <BlankBookPage />
      );
    }
    if (isImageBook) {
      return imageSpread?.right ? (
        <ImageBookPage page={imageSpread.right} number={rightPageNumber} course={course} />
      ) : (
        <BlankBookPage />
      );
    }
    return contentPages[rightIndex] ? (
      <BookPage page={contentPages[rightIndex]} number={rightPageNumber} course={course} />
    ) : (
      <BlankBookPage />
    );
  }

  return null;
}

function BookViewer({ course, onClose }) {
  const readerRef = useRef(null);
  const stageRef = useRef(null);
  const courseId = course.id || course._id;
  const storageKey = `math-book-spread-${courseId}`;

  // Restore last-read spread from localStorage
  const [spread, setSpread] = useState(() => {
    const saved = parseInt(localStorage.getItem(`math-book-spread-${course.id || course._id}`), 10);
    return isNaN(saved) ? 0 : saved;
  });
  const [turningState, setTurningState] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const lastWheel = useRef(0);
  const touchStartX = useRef(0);
  const turnTimeoutRef = useRef(null);
  // Page navigator input state
  const [pageInput, setPageInput] = useState('');
  const [pageInputFocused, setPageInputFocused] = useState(false);

  const contentPages = course.contentPages?.length ? course.contentPages : BOOK_PAGES;
  const isPdfBook = Boolean(
    course.textbookUrl
    && course.textbookPageCount >= 2
    && (course.textbookMimeType === "application/pdf" || /\.pdf(?:$|\?)/i.test(course.textbookUrl)),
  );
  const imageSpreads = buildImageSpreads(course.textbookPages || []);
  const isImageBook = !isPdfBook && imageSpreads.length > 0;
  const maxSpread = isPdfBook
    ? Math.max(0, Math.floor((course.textbookPageCount - 2) / 2))
    : isImageBook
      ? imageSpreads.length - 1
      : Math.ceil(contentPages.length / 2) - 1;

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (readerRef.current?.requestFullscreen) {
        readerRef.current.requestFullscreen().catch(() => { });
      } else if (readerRef.current?.webkitRequestFullscreen) {
        readerRef.current.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => { });
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  const goTo = useCallback(
    (next) => {
      const target = Math.max(0, Math.min(maxSpread, next));
      if (target === spread || turningState !== null) return;

      const dir = target > spread ? "forward" : "backward";
      const from = spread;

      setTurningState({ direction: dir, fromSpread: from, toSpread: target });
      setSpread(target);
      // Persist last-read spread
      localStorage.setItem(storageKey, String(target));

      if (turnTimeoutRef.current) clearTimeout(turnTimeoutRef.current);
      turnTimeoutRef.current = setTimeout(() => {
        setTurningState(null);
      }, 660);
    },
    [maxSpread, spread, turningState, storageKey],
  );

  // Compute left/right page numbers for the current spread (1-based)
  const leftPageNum = isPdfBook ? spread * 2 + 2 : spread * 2 + 1;
  const rightPageNum = isPdfBook ? spread * 2 + 3 : spread * 2 + 2;
  const totalPageCount = isPdfBook
    ? course.textbookPageCount
    : isImageBook
      ? imageSpreads.length * 2
      : contentPages.length;

  // Page navigator: jump by entering a raw page number
  const handlePageInputCommit = () => {
    const raw = parseInt(pageInput, 10);
    if (!isNaN(raw) && raw >= 1 && raw <= totalPageCount) {
      const targetSpread = isPdfBook
        ? Math.floor((raw - 2) / 2)
        : Math.floor((raw - 1) / 2);
      goTo(Math.max(0, targetSpread));
    }
    setPageInput('');
    setPageInputFocused(false);
  };

  useEffect(() => {
    return () => {
      if (turnTimeoutRef.current) clearTimeout(turnTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (document.fullscreenElement || document.webkitFullscreenElement) {
          if (document.exitFullscreen) {
            document.exitFullscreen().catch(() => { });
          }
        } else {
          onClose();
        }
      }
      if (event.key === "ArrowRight") goTo(spread + 1);
      if (event.key === "ArrowLeft") goTo(spread - 1);
    };
    const onWheel = (event) => {
      event.preventDefault();
      if (Math.abs(event.deltaY) < 12 || Date.now() - lastWheel.current < 500) return;
      lastWheel.current = Date.now();
      goTo(spread + (event.deltaY > 0 ? 1 : -1));
    };
    window.addEventListener("keydown", onKeyDown);
    const stageEl = stageRef.current;
    if (stageEl) stageEl.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (stageEl) stageEl.removeEventListener("wheel", onWheel);
    };
  }, [goTo, onClose, spread]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    const diffX = e.changedTouches[0].clientX - touchStartX.current;
    if (diffX < -50) {
      goTo(spread + 1);
    } else if (diffX > 50) {
      goTo(spread - 1);
    }
  };

  const isTurning = turningState !== null;
  // During animation: base shows the DESTINATION spread so new content is
  // already visible underneath the leafFront overlay that wipes away.
  const baseSpread = isTurning ? turningState.toSpread : spread;

  const fromSpread = isTurning ? turningState.fromSpread : null;
  const fromImageSpread = isImageBook && fromSpread !== null ? imageSpreads[fromSpread] : null;
  const baseImageSpread = isImageBook ? imageSpreads[baseSpread] : null;

  return (
    <section
      ref={readerRef}
      className={`${styles.reader} ${isFullscreen ? styles.readerIsFullscreen : ""}`}
      aria-label={`${course.level} book reader`}
    >
      <header className={styles.readerTopbar}>
        <button type="button" className={styles.readerClose} onClick={onClose}>
          <X size={17} /> Close <span>Esc</span>
        </button>

        <div className={styles.readerTitle}>
          <BookOpen size={17} />
          <span>
            {course.level} · {course.title}
          </span>
        </div>


        {/* Page navigator between Close and Title */}
        <div className={styles.pageNavigator}>
          {pageInputFocused ? (
            <input
              className={styles.pageNavInput}
              type="text"
              inputMode="numeric"
              value={pageInput}
              autoFocus
              onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ''))}
              onBlur={handlePageInputCommit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handlePageInputCommit();
                if (e.key === 'Escape') { setPageInput(''); setPageInputFocused(false); }
              }}
              aria-label="Go to page number"
            />
          ) : (
            <button
              type="button"
              className={styles.pageNavDisplay}
              onClick={() => { setPageInput(''); setPageInputFocused(true); }}
              title="Click to jump to a page"
            >
              {leftPageNum}–{Math.min(rightPageNum, totalPageCount)}
              <span className={styles.pageNavSep}>/</span>
              {totalPageCount}
            </button>
          )}
        </div>

        <div className={styles.topbarControls}>
          <button
            type="button"
            className={styles.fullscreenBtn}
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Full Screen"}
          >
            {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            <span>{isFullscreen ? "Exit Fullscreen" : "Full Screen"}</span>
          </button>
        </div>
      </header>

      <div
        ref={stageRef}
        className={styles.readerStage}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button
          type="button"
          className={`${styles.pageArrow} ${styles.pageArrowLeft}`}
          disabled={spread === 0 || isTurning}
          onClick={() => goTo(spread - 1)}
          aria-label="Previous pages"
        >
          <ChevronLeft />
        </button>

        <div className={styles.book}>
          <div className={styles.bookSpine} />

          {/* Base left page — always the destination spread */}
          {isImageBook && baseImageSpread?.spread ? (
            <ImageSpread page={baseImageSpread.spread} number={baseSpread * 2 + 1} course={course} />
          ) : (
            <PageSlot
              side="left"
              spreadIndex={baseSpread}
              course={course}
              contentPages={contentPages}
              isPdfBook={isPdfBook}
              isImageBook={isImageBook}
              imageSpreads={imageSpreads}
            />
          )}

          {/* Base right page — always the destination spread */}
          {(!isImageBook || !baseImageSpread?.spread) && (
            <PageSlot
              side="right"
              spreadIndex={baseSpread}
              course={course}
              contentPages={contentPages}
              isPdfBook={isPdfBook}
              isImageBook={isImageBook}
              imageSpreads={imageSpreads}
            />
          )}

          {/* Full-spread scroll wipe overlay — old spread rolls away over the new content */}
          {isTurning && (
            <div
              className={`${styles.turningLeaf} ${turningState.direction === "forward" ? styles.turnForward : styles.turnBackward}`}
            >
              {/* leafFront: full old spread rendered as a two-column grid that wipes away */}
              <div className={styles.leafFront}>
                {isImageBook && fromImageSpread?.spread ? (
                  <ImageSpread page={fromImageSpread.spread} number={fromSpread * 2 + 1} course={course} />
                ) : (
                  <PageSlot
                    side="left"
                    spreadIndex={fromSpread}
                    course={course}
                    contentPages={contentPages}
                    isPdfBook={isPdfBook}
                    isImageBook={isImageBook}
                    imageSpreads={imageSpreads}
                  />
                )}
                {(!isImageBook || !fromImageSpread?.spread) && (
                  <PageSlot
                    side="right"
                    spreadIndex={fromSpread}
                    course={course}
                    contentPages={contentPages}
                    isPdfBook={isPdfBook}
                    isImageBook={isImageBook}
                    imageSpreads={imageSpreads}
                  />
                )}
              </div>
              {/* Cylinder roll-edge highlight that sweeps across the full width */}
              <div className={styles.leafShadow} />
            </div>
          )}
        </div>

        <button
          type="button"
          className={`${styles.pageArrow} ${styles.pageArrowRight}`}
          disabled={spread === maxSpread || isTurning}
          onClick={() => goTo(spread + 1)}
          aria-label="Next pages"
        >
          <ChevronRight />
        </button>
      </div>

      <footer className={styles.readerFooter}>
        <span>Use ← → keys, mouse wheel or drag to turn pages</span>
        <button type="button" className={styles.fullscreenFooterBtn} onClick={toggleFullscreen}>
          {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          <span>{isFullscreen ? "Exit Fullscreen" : "Full Screen"}</span>
        </button>
        <button type="button" onClick={onClose}>
          <Grid2X2 size={15} /> Course library
        </button>
      </footer>
    </section>
  );
}

export default function MathStudy() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [library, setLibrary] = useState(DEFAULT_LIBRARY);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readerOpen, setReaderOpen] = useState(Boolean(courseId));
  const [selectedCourse, setSelectedCourse] = useState(null);
  useEffect(() => {
    Promise.all([getMathLibrary(), getMathCourses()])
      .then(([nextLibrary, nextCourses]) => {
        if (nextLibrary) setLibrary(nextLibrary);
        if (nextCourses) setCourses(nextCourses);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { setReaderOpen(Boolean(courseId)); }, [courseId]);
  const courseFromRoute = courses.find(item => String(item.id || item._id) === String(courseId));
  const course = selectedCourse || courseFromRoute;
  const openCourse = (nextCourse) => {
    const id = nextCourse.id || nextCourse._id;
    setSelectedCourse(nextCourse);
    setReaderOpen(true);
    navigate(`/study/math/course/${id}`);
  };
  const closeReader = () => {
    // Hide immediately even when a router transition is delayed by the parent layout.
    setReaderOpen(false);
    setSelectedCourse(null);
    navigate("/study/math", { replace: true });
  };

  return <>{course && readerOpen ? <BookViewer course={course} onClose={closeReader} /> : <CourseGallery onOpen={openCourse} library={library} courses={courses} loading={loading} />}</>;
}
